import { useMemo } from "react";
import type { AssetFolderSummary, CreateFolderResult, FolderScopeValue } from "./asset-types";
import { FolderPickList, type FolderPickRow } from "./folder-pick-list";
import { buildFolderRows } from "./folder-rows";

export interface FolderMenuProps {
    folders: AssetFolderSummary[];
    scope: FolderScopeValue;
    canManage: boolean;
    onScopeChange: (scope: FolderScopeValue) => void;
    onCreateFolder: (name: string) => Promise<CreateFolderResult>;
    onClose: () => void;
}

/**
 * The menu behind the scope row's overflow chip (spec §5.3). It carries every
 * scope the row can express — including `All` and `Unsorted`, which are pinned
 * chips out there but can still be off-screen at a narrow width — plus folder
 * creation at its foot.
 *
 * Choosing a row sets the scope and closes. Renaming and deleting a folder are
 * not here: they live on the chip's own caret (spec §5.5), where the folder
 * being acted on is the one under the pointer rather than a row in a list.
 *
 * The menu is taller than the picker and escapes its bounds. That is ordinary
 * for an anchored menu, and it is why Escape ordering (spec §14) is
 * load-bearing rather than pedantic.
 */
export function FolderMenu({ folders, scope, canManage, onScopeChange, onCreateFolder, onClose }: FolderMenuProps) {
    const rows = useMemo<FolderPickRow[]>(() => buildFolderRows(folders, { includeAll: true }), [folders]);

    return (
        <FolderPickList
            rows={rows}
            selected={scope}
            canCreate={canManage}
            title="Folders"
            searchLabel="Search folders"
            onChoose={(value) => {
                onScopeChange(value);
                onClose();
            }}
            onCreate={onCreateFolder}
            onClose={onClose}
        />
    );
}
